/**
 * "Colar no mural" — transforma uma mensagem do chat em post-it.
 *
 * Reaproveita o formulário do mural (`NewMuralNoteModal`) com o texto já
 * preenchido e, se a mensagem tinha paciente vinculado, o paciente também.
 * A pessoa ainda revisa antes de colar: recado de chat costuma vir com
 * "oi, bom dia" e o mural é pra ler de relance.
 *
 * O post-it guarda um snapshot da origem (`fromChat`) — quem mandou e
 * quando — porque a mensagem pode ser apagada do chat e o recado não.
 *
 * Quando quem cola é o MÉDICO, a recepção recebe um pop-up na tela
 * ([[MuralAlertWatcher]]) — o recado foi pro mural, mas o médico quer que
 * alguém do balcão saiba agora, não quando passar o olho no quadro.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { canSeeMural } from '@/hooks/useMuralNotes';
import { canSee } from '@/hooks/usePendencias';
import { MuralNoteService } from '@/services/firebase/muralNotes';
import type { NewMuralNote } from '@/services/firebase/muralNotes';
import { PatientService } from '@/services/firebase/patients';
import type { ChatMessageData } from '@/services/firebase/chat';
import { NewMuralNoteModal } from '@/pages/mural/NewMuralNoteModal';
import { MURAL_ROLE_LABEL } from '@/pages/mural/muralUi';
import type { Patient } from '@/types/models';

/** Só mensagens com texto viram post-it (imagem/arquivo/atenção não). */
export function canPinToMural(m: Pick<ChatMessageData, 'type' | 'text'>): boolean {
  return (!m.type || m.type === 'text') && !!m.text?.trim();
}

/** Quem pode colar no mural é quem enxerga o mural. */
export function useCanPinToMural(): boolean {
  const { role } = useAuth();
  return canSeeMural(role);
}

interface Props {
  clinicId: string;
  message: ChatMessageData | null;
  /** Ex.: "#recepção". Não passe nome de DM — o mural é de todos. */
  channelName?: string;
  onClose: () => void;
}

export const ChatToMuralModal = ({ clinicId, message, channelName, onClose }: Props) => {
  const { user, profile, role } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [done, setDone] = useState(false);
  // Recepção cuida do mural — só o post-it colado por quem NÃO é da recepção
  // (o médico) pipoca na tela dela. Mesma lista de papéis do CallNextWatcher.
  const alertReception = !canSee(role, 'BRIEFING_NEEDED');

  // Paciente vinculado à mensagem → já vem escolhido no formulário. O modal
  // só abre depois dessa busca: se o paciente chegasse com o formulário
  // aberto, o reset do formulário apagaria o que a pessoa já digitou.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setPatient(null);
    const pid = message?.linkedPatientId;
    if (!pid) {
      setReady(true);
      return;
    }
    setReady(false);
    let cancelled = false;
    PatientService.getPatient(clinicId, pid)
      .then((p) => {
        if (!cancelled && p) setPatient(p as Patient);
      })
      .catch((err) => console.error('[chat→mural] paciente vinculado:', err))
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, message?.linkedPatientId]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 2500);
    return () => clearTimeout(t);
  }, [done]);

  const actor = useMemo(
    () => ({
      uid: user?.uid ?? '',
      name: profile?.displayName ?? undefined,
      roleLabel: role ? MURAL_ROLE_LABEL[role] : undefined,
    }),
    [user?.uid, profile?.displayName, role],
  );

  const create = async (data: NewMuralNote) => {
    if (!message) return;
    try {
      await MuralNoteService.create(
        clinicId,
        {
          ...data,
          fromChat: {
            channelId: message.channelId,
            messageId: message.id,
            channelName,
            senderName: message.senderName,
            sentAt: message.timestamp,
          },
          receptionAlert: alertReception || undefined,
        },
        actor,
      );
      setDone(true);
    } catch (err) {
      console.error('[chat→mural] criar:', err);
      window.alert('Não consegui colar no mural. Tente de novo.');
      throw err;
    }
  };

  const when = message
    ? new Date(message.timestamp).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      })
    : '';

  return (
    <>
      <NewMuralNoteModal
        isOpen={!!message && ready}
        onClose={onClose}
        clinicId={clinicId}
        initialPatient={patient}
        initialText={message?.text ?? ''}
        sourceHint={
          message
            ? `Do chat${channelName ? ` ${channelName}` : ''} · ${message.senderName} · ${when}. Revise o texto antes de colar.${
                alertReception ? ' A recepção recebe um aviso na tela.' : ''
              }`
            : undefined
        }
        onCreate={create}
      />
      {done &&
        createPortal(
          <div
            role="status"
            className="fixed bottom-6 left-1/2 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-full bg-on-surface px-4 py-2 text-sm text-surface shadow-float animate-fade-in"
          >
            <Check size={14} /> Colado no mural{alertReception ? ' · recepção avisada' : ''}
          </div>,
          document.body,
        )}
    </>
  );
};
